import { cva, type VariantProps } from 'class-variance-authority';
import { ExternalLink, Globe } from 'lucide-react';
import * as React from 'react';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// =============================================================================
// Variants
// =============================================================================

const sourceVariants = cva(
  'inline-flex items-center gap-1.5 transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
  {
    variants: {
      variant: {
        default:
          'text-sm text-primary hover:text-primary underline underline-offset-2 rounded-sm',
        pill: 'px-2.5 py-1 rounded-full bg-accent hover:bg-surface-2 text-xs text-secondary-foreground',
        minimal:
          'text-xs text-secondary-foreground hover:text-foreground rounded-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const DEFAULT_MAX_VISIBLE_SOURCES = 3;

function getMaxVisibleSources(maxVisible: number): number {
  return Number.isFinite(maxVisible) && maxVisible >= 0
    ? Math.floor(maxVisible)
    : DEFAULT_MAX_VISIBLE_SOURCES;
}

// =============================================================================
// Types
// =============================================================================

export interface SourceData {
  /** Source URL */
  url: string;
  /** Display title */
  title?: string;
  /** Source description */
  description?: string;
  /** Favicon URL */
  favicon?: string;
  /** Site name */
  siteName?: string;
}

// =============================================================================
// Source Favicon
// =============================================================================

interface SourceFaviconProps {
  url: string;
  favicon?: string;
  className?: string;
}

function SourceFavicon({ url, favicon, className }: SourceFaviconProps) {
  // Generate favicon URL from domain if not provided
  const faviconUrl = React.useMemo(() => {
    if (favicon) return favicon;
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch {
      return null;
    }
  }, [url, favicon]);

  // Keyed by URL so a new favicon starts in its own loading state.
  return (
    <FaviconSlot
      key={faviconUrl ?? ''}
      faviconUrl={faviconUrl}
      className={className}
    />
  );
}

/**
 * Fixed 16px slot: the icon and the Globe fallback share one box, so load or
 * failure only changes opacity (token-backed, instant under reduced motion).
 */
function FaviconSlot({
  faviconUrl,
  className,
}: {
  faviconUrl: string | null;
  className?: string;
}) {
  const [status, setStatus] = React.useState<'loading' | 'loaded' | 'error'>(
    faviconUrl ? 'loading' : 'error',
  );
  const fade =
    'transition-opacity duration-(--motion-duration-fast) ease-(--motion-easing-smooth) motion-reduce:transition-none';

  return (
    <span
      data-slot="ai-source-favicon"
      className={cn('relative inline-block size-4 shrink-0', className)}
    >
      <Globe
        aria-hidden="true"
        className={cn(
          'absolute inset-0 size-4 text-muted-foreground',
          fade,
          status === 'error' ? 'opacity-100' : 'opacity-0',
        )}
      />
      {faviconUrl && status !== 'error' && (
        <img
          ref={(img) => {
            // Cached images can finish before React attaches onLoad.
            if (img?.complete && img.naturalWidth > 0) setStatus('loaded');
          }}
          src={faviconUrl}
          alt=""
          className={cn(
            'absolute inset-0 size-4 rounded',
            fade,
            status === 'loaded' ? 'opacity-100' : 'opacity-0',
          )}
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
        />
      )}
    </span>
  );
}

SourceFavicon.displayName = 'SourceFavicon';

// =============================================================================
// Source Trigger (Clickable element)
// =============================================================================

export interface AISourceTriggerProps
  extends VariantProps<typeof sourceVariants> {
  /** Source data */
  source: SourceData;
  /** Whether to show favicon */
  showFavicon?: boolean;
  /** Additional class name */
  className?: string;
  /** Click handler */
  onClick?: () => void;
}

function AISourceTrigger({
  source,
  variant,
  showFavicon = true,
  className,
  onClick,
}: AISourceTriggerProps) {
  const displayText = source.title || source.siteName || source.url;

  const sharedClassName = cn(sourceVariants({ variant }), className);
  const sharedChildren = (
    <>
      {showFavicon && (
        <SourceFavicon url={source.url} favicon={source.favicon} />
      )}
      <span className="truncate max-w-[200px]">{displayText}</span>
      {variant === 'default' && (
        <ExternalLink className="size-3 shrink-0" aria-hidden="true" />
      )}
    </>
  );

  // Render as <a> when navigating to URL (proper semantics)
  if (!onClick) {
    return (
      <a
        data-slot="ai-source-trigger"
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        className={sharedClassName}
        aria-label={`Source: ${displayText}`}
      >
        {sharedChildren}
      </a>
    );
  }

  // Render as <button> when custom click handler is provided
  return (
    <button
      type="button"
      data-slot="ai-source-trigger"
      onClick={onClick}
      className={sharedClassName}
      aria-label={`Source: ${displayText}`}
    >
      {sharedChildren}
    </button>
  );
}

AISourceTrigger.displayName = 'AISourceTrigger';

// =============================================================================
// Source Content (Tooltip/Popover content)
// =============================================================================

export interface AISourceContentProps {
  /** Source data */
  source: SourceData;
  /** Additional class name */
  className?: string;
}

function AISourceContent({ source, className }: AISourceContentProps) {
  const domain = React.useMemo(() => {
    try {
      return new URL(source.url).hostname;
    } catch {
      return source.url;
    }
  }, [source.url]);

  return (
    <div className={cn('p-3 max-w-xs space-y-2', className)}>
      <div className="flex items-start gap-2">
        <SourceFavicon
          url={source.url}
          favicon={source.favicon}
          className="mt-0.5 [&_svg]:text-background/70"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-background truncate">
            {source.title || domain}
          </p>
          <p className="text-xs text-background/70 truncate">{domain}</p>
        </div>
      </div>
      {source.description && (
        <p className="text-xs text-background/70 line-clamp-2">
          {source.description}
        </p>
      )}
      <a
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-xs text-background underline underline-offset-2"
      >
        Visit source
        <ExternalLink className="size-3" />
      </a>
    </div>
  );
}

AISourceContent.displayName = 'AISourceContent';

// =============================================================================
// Main AISource Component (with built-in tooltip)
// =============================================================================

export interface AISourceProps extends VariantProps<typeof sourceVariants> {
  /** Source data */
  source: SourceData;
  /** Whether to show favicon */
  showFavicon?: boolean;
  /** Whether to show tooltip on hover */
  showTooltip?: boolean;
  /** Additional class name */
  className?: string;
}

function AISource({
  source,
  variant,
  showFavicon = true,
  showTooltip = true,
  className,
}: AISourceProps) {
  if (!showTooltip) {
    return (
      <span data-slot="ai-source">
        <AISourceTrigger
          source={source}
          variant={variant}
          showFavicon={showFavicon}
          className={className}
        />
      </span>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span data-slot="ai-source">
            <AISourceTrigger
              source={source}
              variant={variant}
              showFavicon={showFavicon}
              className={className}
            />
          </span>
        }
      />
      <TooltipPopup side="top" className="p-0">
        <AISourceContent source={source} />
      </TooltipPopup>
    </Tooltip>
  );
}

AISource.displayName = 'AISource';

// =============================================================================
// Source List Component
// =============================================================================

export interface AISourceListProps {
  /** Array of sources */
  sources: SourceData[];
  /** Maximum sources to show before truncating */
  maxVisible?: number;
  /** Whether to show expanded list */
  showAll?: boolean;
  /** Callback when "show more" is clicked */
  onShowMore?: () => void;
  /** Additional class name */
  className?: string;
}

function AISourceList({
  sources,
  maxVisible = 3,
  showAll = false,
  onShowMore,
  className,
}: AISourceListProps) {
  const safeMaxVisible = getMaxVisibleSources(maxVisible);
  const visibleSources = showAll ? sources : sources.slice(0, safeMaxVisible);
  const remainingCount = Math.max(0, sources.length - safeMaxVisible);
  const navRef = React.useRef<HTMLElement>(null);
  const handoffRef = React.useRef(false);

  // React detaches refs before removing the node, so activeElement is still the
  // button here: hand off only if the button held focus at the moment it left.
  const showMoreRef = React.useCallback((node: HTMLButtonElement | null) => {
    if (!node) return;
    return () => {
      handoffRef.current = node.ownerDocument.activeElement === node;
    };
  }, []);

  // Runs every commit so a flag from a removal that did not reveal sources
  // (showAll still false) is dropped and cannot leak into a later expansion.
  React.useLayoutEffect(() => {
    const handoff = handoffRef.current;
    handoffRef.current = false;
    if (!showAll || !handoff) return;
    const nav = navRef.current;
    if (nav?.ownerDocument.activeElement !== nav?.ownerDocument.body) return;
    nav
      ?.querySelectorAll<HTMLElement>('[data-slot="ai-source-trigger"]')
      [safeMaxVisible]?.focus({ preventScroll: true });
  });

  return (
    <nav
      ref={navRef}
      data-slot="ai-source-list"
      aria-label="Sources"
      className={cn('flex flex-wrap items-center gap-2', className)}
    >
      <span className="text-xs text-secondary-foreground">Sources:</span>
      {visibleSources.map((source, index) => (
        <AISource
          key={`${source.url}-${index}`}
          source={source}
          variant="pill"
        />
      ))}
      {!showAll && remainingCount > 0 && (
        <button
          ref={showMoreRef}
          type="button"
          onClick={onShowMore}
          aria-label={`Show ${remainingCount} more sources`}
          className="px-2.5 py-1 rounded-full bg-accent hover:bg-surface-2 text-xs text-secondary-foreground transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          +{remainingCount} more
        </button>
      )}
    </nav>
  );
}

AISourceList.displayName = 'AISourceList';

export {
  AISource,
  AISourceContent,
  AISourceList,
  AISourceTrigger,
  SourceFavicon,
  sourceVariants,
};
